import type { AgentSubscription } from "schemas/agent-billing";
import type { SubscriptionDetails } from "schemas/generated/billing";
import type { Workspace } from "schemas/generated/workspace";

import { isShopifyReviewerAccount } from "replo-utils/lib/shopifyBilling";
import {
  AGENT_FREE_MONTH_MINIMUM_PAID_TENURE_MS,
  isAgentFreeMonthPlanEligible,
} from "schemas/agent-billing";

type WorkspaceMembership = Workspace["workspaceMemberships"][number];
type AgentFreeMonthWorkspace = Pick<
  Workspace,
  "defaultProjectType" | "paymentProcessor"
> & {
  workspaceMemberships: (Pick<WorkspaceMembership, "archivedAt" | "role"> & {
    user: Pick<WorkspaceMembership["user"], "email" | "firstName" | "lastName">;
  })[];
};

export function isShopifyAgentFreeMonthOfferEligible({
  subscription,
  workspace,
  now = new Date(),
}: {
  subscription: Pick<
    SubscriptionDetails,
    | "billingInterval"
    | "billingTier"
    | "endedAt"
    | "isManagedByReplo"
    | "paymentProcessor"
    | "startedAt"
  >;
  workspace: AgentFreeMonthWorkspace;
  now?: Date;
}): boolean {
  const hasReviewerOwner = workspace.workspaceMemberships.some((membership) => {
    return (
      membership.role === "owner" &&
      !membership.archivedAt &&
      isShopifyReviewerAccount(membership.user)
    );
  });

  return (
    subscription.endedAt === null &&
    isAgentFreeMonthPlanEligible({
      interval: subscription.billingInterval,
      plan: subscription.billingTier,
      isManaged: subscription.isManagedByReplo,
    }) &&
    subscription.paymentProcessor === "shopify" &&
    workspace.paymentProcessor === "shopify" &&
    workspace.defaultProjectType !== "agent" &&
    !hasReviewerOwner &&
    now.getTime() - subscription.startedAt.getTime() >=
      AGENT_FREE_MONTH_MINIMUM_PAID_TENURE_MS
  );
}

export function isAgentBillingFreeMonthOfferEligible({
  subscription,
  workspace,
}: {
  subscription: AgentSubscription;
  workspace: AgentFreeMonthWorkspace;
}): boolean {
  return (
    (subscription.status === "active" ||
      subscription.status === "trialing" ||
      subscription.status === "past_due") &&
    isAgentFreeMonthPlanEligible({
      interval: subscription.interval,
      plan: subscription.plan,
      isManaged: subscription.hasCustomPricing,
    }) &&
    subscription.stripeSubscriptionId.length > 0 &&
    workspace.paymentProcessor !== "shopify" &&
    workspace.defaultProjectType !== "agent"
  );
}
